import { oc } from "@orpc/contract";
import { z } from "zod";

const PersonSchema = z.object({
    id: z.string(),
    email: z.string(),
    displayName: z.string(),
    lastName: z.string(),
    jobTitle: z.string(),
    imageUrl: z.string().optional(),
    workLocationId: z.string(),
    workLocationLabel: z.string(),
    departmentId: z.string(),
    departmentLabel: z.string()
});

export type Person = z.infer<typeof PersonSchema>;

export const PeopleSortColumns = ["lastName", "jobTitle", "departmentLabel", "workLocationLabel"] as const;

export type PeopleSortColumn = (typeof PeopleSortColumns)[number];

const ListPeopleInputSchema = z.object({
    orgId: z.string(),
    continuationToken: z.string().optional(),
    pageSize: z.number().default(25),
    q: z.string().optional(),
    departmentIds: z.array(z.string()).optional(),
    workLocationIds: z.array(z.string()).optional(),
    sortBy: z.enum(PeopleSortColumns).default("lastName"),
    sortDir: z.enum(["asc", "desc"]).default("asc"),
    includeSelf: z.boolean().optional()
});

const ListPeopleOutputSchema = z.object({
    items: z.array(PersonSchema),
    continuationToken: z.string().optional(),
    totalCount: z.number(),
    self: PersonSchema.optional()
});

export type ListPeopleInput = z.infer<typeof ListPeopleInputSchema>;
export type ListPeopleOutput = z.infer<typeof ListPeopleOutputSchema>;

const FacetSchema = z.object({
    id: z.string(),
    label: z.string()
});

const ListFacetsInputSchema = z.object({
    orgId: z.string()
});

const ListFacetsOutputSchema = z.object({
    departments: z.array(FacetSchema),
    workLocations: z.array(FacetSchema)
});

export type Facet = z.infer<typeof FacetSchema>;
export type ListFacetsOutput = z.infer<typeof ListFacetsOutputSchema>;

const ProfileManagerSchema = z.object({
    id: z.string(),
    displayName: z.string(),
    imageUrl: z.string().optional()
});

const ProfileDirectReportSchema = z.object({
    id: z.string(),
    displayName: z.string(),
    jobTitle: z.string().optional(),
    imageUrl: z.string().optional()
});

const ProfilePersonSchema = z.object({
    id: z.string(),
    displayName: z.string(),
    pronouns: z.string().optional(),
    jobTitle: z.string().optional(),
    departmentId: z.string().optional(),
    departmentLabel: z.string().optional(),
    workLocationId: z.string().optional(),
    workLocationLabel: z.string().optional(),
    hireDate: z.string().optional(),
    isNewHire: z.boolean(),
    timeZone: z.string().optional(),
    email: z.string().optional(),
    bio: z.string().optional(),
    interests: z.array(z.string()).default([]),
    competencies: z.array(z.string()).default([]),
    imageUrl: z.string().optional(),
    manager: ProfileManagerSchema.optional()
});

const ProfileInCommonSchema = z.object({
    location: z.string().optional(),
    department: z.string().optional(),
    competencies: z.array(z.string()).default([]),
    interests: z.array(z.string()).default([]),
    sameManagerName: z.string().optional()
});

const GetProfileInputSchema = z.object({
    personId: z.string()
});

const GetProfileOutputSchema = z.object({
    person: ProfilePersonSchema,
    directReports: z.array(ProfileDirectReportSchema),
    inCommon: ProfileInCommonSchema,
    isSelf: z.boolean(),
    canViewFeedback: z.boolean(),
    canViewReviews: z.boolean()
});

const ProfileNotFoundSchema = z.object({
    personId: z.string()
});

const GetMyCompetenciesOutputSchema = z.object({
    competencies: z.array(z.string()).default([])
});

export type GetMyCompetenciesOutput = z.infer<typeof GetMyCompetenciesOutputSchema>;

export type ProfileManager = z.infer<typeof ProfileManagerSchema>;
export type ProfileDirectReport = z.infer<typeof ProfileDirectReportSchema>;
export type ProfilePerson = z.infer<typeof ProfilePersonSchema>;
export type ProfileInCommon = z.infer<typeof ProfileInCommonSchema>;
export type GetProfileOutput = z.infer<typeof GetProfileOutputSchema>;

const profileErrors = {
    NOT_FOUND: { message: "Profile not found", data: ProfileNotFoundSchema }
} as const;

export const directoryContract = {
    listMembers: oc.route({ method: "GET", path: "/list-members" }).input(ListPeopleInputSchema).output(ListPeopleOutputSchema),
    listFacets: oc.route({ method: "GET", path: "/list-facets" }).input(ListFacetsInputSchema).output(ListFacetsOutputSchema),
    getProfile: oc.route({ method: "GET", path: "/get-profile" }).errors(profileErrors).input(GetProfileInputSchema).output(GetProfileOutputSchema),
    getMyCompetencies: oc.route({ method: "GET", path: "/get-my-competencies" }).errors({ UPSTREAM_FAILURE: {} }).output(GetMyCompetenciesOutputSchema)
};
